import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 dark:bg-destructive/60',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
        success: 'bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white',
        manage: 'bg-[#922fa4] hover:bg-[#7a2788] text-white',
        action: 'bg-accent-purple hover:bg-interaction-hover active:bg-interaction-active',
        // The portal's two buttons: the filled call to action the learner home uses
        // ("Browse courses") and its outlined companion ("Explore lessons"). Stated
        // here rather than as Tailwind strings repeated across a feature, so a tweak
        // to the portal button is one edit.
        portal:
          'rounded-lg bg-(--accent-purple) font-semibold text-white shadow-md transition-all hover:bg-(--accent-purple)/90 hover:shadow-lg active:scale-[0.98] disabled:shadow-none',
        portalOutline:
          'rounded-lg border-2 border-(--accent-purple) bg-white font-semibold text-(--accent-purple) transition-all hover:bg-(--accent-purple)/5 active:scale-[0.98]'
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        rd: 'h-10 rounded-full py-6 has-[>svg]:px-4 w-full text-xl font-normal',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return <Comp data-slot='button' className={cn(buttonVariants({ variant, size, className }))} {...props} />
}

export { Button, buttonVariants }
